<template>
  <section class="screen" :class="modifiers">
    <header class="screen__header">
      <div v-if="$slots.title || title" class="screen__title">
        <BaseTitle
          v-if="title"
          v-bind="title"
        />
        <slot name="title" />
      </div>
      <div v-if="$slots.actions" class="screen__actions">
        <slot name="actions" />
      </div>
    </header>
    <div
      v-if="$slots.middle"
      class="screen__content screen__content_middle"
    >
      <slot name="middle" />
    </div>
    <div v-else class="screen__content">
      <slot />
    </div>
    <div
      v-if="isSpinnerVisible"
      class="screen__mask"
    >
      <BaseSpinner :size="60" :stroke="4" class="screen__spinner" />
      <span v-if="pendingText" v-html="pendingText" />
    </div>
    <slot name="root" />
  </section>
</template>

<script>
import BaseTitle from '@/components/BaseTitle.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';

export default {
  name: 'BaseScreen',
  components: {
    BaseTitle,
    BaseSpinner,
  },
  props: {
    title: {
      type: Object,
      default: null,
    },
    pendingText: {
      type: String,
      default: '',
    },
    isHeaderHidden: {
      type: Boolean,
      default: false,
    },
    isFooterHidden: {
      type: Boolean,
      default: false,
    },
    isSpinnerVisible: {
      type: Boolean,
      default: false,
    },
    isFull: {
      type: Boolean,
      default: false,
    },
    isPatternBackground: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    modifiers() {
      return {
        screen_header_hidden: this.isHeaderHidden,
        screen_footer_hidden: this.isFooterHidden,
        screen_pending: this.isSpinnerVisible,
        screen_full: this.isFull,
        screen_pattern: this.isPatternBackground,
      };
    },
  },
};
</script>

<style lang="stylus">
@keyframes slide-out
  0%
    opacity 1
    transform translate3d(0, 0, 0)
  100%
    opacity 0
    transform translate3d(-50%, 0, 0)

@keyframes slide-out-back
  0%
    opacity 1
    transform translate3d(0, 0, 0)
  100%
    opacity 0
    transform translate3d(50%, 0, 0)

@keyframes slide-in
  0%
    opacity 0
    transform translate3d(50%, 0, 0)
  100%
    opacity 1
    transform translate3d(0, 0, 0)

@keyframes slide-in-back
  0%
    opacity 0
    transform translate3d(-50%, 0, 0)
  100%
    opacity 1
    transform translate3d(0, 0, 0)

.screen
  display flex
  flex-direction column
  width 100%
  height 100%
  overflow hidden
  background-color $c_bg

  &-slide-leave-active
    animation slide-out 500ms ease both
    .app_back_move &
      animation-name slide-out-back

  &-slide-enter-active
    animation slide-in 500ms ease both
    .app_back_move &
      animation-name slide-in-back

  &-slide-enter-active
  &-slide-leave-active
    abs top left right bottom
    overflow hidden
    pointer-events none
    *
      animation-play-state paused !important

  &_pattern
    background linear-gradient(180deg, rgba(15, 18, 31, 0.3) 0%, rgba(10, 146, 165, 0.6) 18.75%, #fff 100%), url('~@/assets/images/pattern.svg') no-repeat;
    background-size cover
    .menu-item:before
      background-color $c_tip
      opacity 0.4

  &__header
    position relative
    display flex
    align-items center
    justify-content center
    height $header_height
    padding-left $side_padding
    padding-right @padding-left
    ~/_header_hidden &
      visibility hidden
    ~/_full &
      display none

  &__content
    flex 1
    min-height 0
    padding-top 0.5em
    padding-bottom @padding-top + $footer_height
    padding-left $side_padding
    padding-right $side_padding

    &_middle
      display flex
      align-items center
      justify-content center
    ~/_footer_hidden &
      padding-top 0
      padding-bottom $side_padding
    ~/_header_hidden &
      padding-bottom @padding-top + $footer_height
    ~/_full &
      padding 0

  &__actions
    abs top bottom right
    display flex
    align-items center
    padding-right $side_padding
    z-index 1
    .button
      margin-left 0.5em

  &__overlay
    fix top left right bottom
    z-index 100

  &__mask
    abs top left right bottom
    display flex
    align-items center
    justify-content center
    background-color $c_bg
    font-family $font_secondary
    font-size 1.5em
    line-height 1.2
    color $c_text
    z-index 10

  &__spinner
    color $c_primary

  &__offset
    margin-right ($side_padding * -1)
    margin-left ($side_padding * -1)

  &__offset-right
    margin-right ($side_padding * -1)

  &__offset-left
    margin-left ($side_padding * -1)

  &__stretch
    height 100%
    &.pretty-scrollbar
      margin-right ($side_padding * -1)
      margin-left ($side_padding * -1)
      padding-left $side_padding
      padding-right 'calc(%s - %s)' % ($side_padding ($scroll_width * 1px))

  &__title
    display flex
    align-items center
    justify-content center
    text-align center
    .title
      font-size 1.4em

  &__form
    max-width 36em
    margin 0 auto
    padding 2em 0
    &_no_padding
      padding 0

  &__gap
    margin-top 2em
    &_half
      margin-top 1.25em
    &_double
      margin-top 3em

  &__row
    display flex
    &_column
      flex-direction column

  &__col
    flex-grow 1
    flex-basis 0
    max-width 100%
    &_side
      max-width 35%
      padding-right 2em
    ~/__row_column &
      flex-grow 0
      flex-basis auto
    ~/__row_column &_stretch
      flex 1
      min-height 0

  &__logo
    width auto
    height 2.5em
    margin 0 auto
    padding 0
    background none
    border 0
    outline none
    &_large&
      width 36em
      height auto
    .icon
      display block
      width 100%
      height 100%

  &__rate
    color $c_light
    max-width 24em
    margin-right auto
    margin-left auto
    padding-top 2em

  &__wait
    text-align center

  &__faq
    display flex
    flex-wrap wrap
    padding 2em 0

  &__question
    border-radius 10em
    margin 0.8em 1.6em 0.8em 0
</style>
